/**
 * User labels travel as IMAP keywords `TIK_<hex of UTF-8 name>`. The server writes uppercase
 * hex; decoding accepts either case and rejects odd length / invalid UTF-8 (spec §15).
 */
export const LABEL_PREFIX = 'TIK_';
export const LABEL_MAX_LEN = 40;

/** `"مهم"` → `"TIK_D985D987D985"`. */
export function encodeLabel(name: string): string {
  const bytes = new TextEncoder().encode(name);
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0').toUpperCase();
  return LABEL_PREFIX + hex;
}

/** Decode a keyword; returns null for anything that is not a well-formed label flag. */
export function decodeLabel(flag: string): string | null {
  if (!flag.startsWith(LABEL_PREFIX)) return null;
  const hex = flag.slice(LABEL_PREFIX.length);
  if (!/^[0-9A-Fa-f]+$/.test(hex) || hex.length % 2) return null;
  try {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes) || null;
  } catch {
    return null;
  }
}

/** Every decodable label in a flag list, in flag order. */
export function labelsOf(flags: readonly string[] | null | undefined): string[] {
  const out: string[] = [];
  for (const f of flags ?? []) {
    const name = decodeLabel(f);
    if (name) out.push(name);
  }
  return out;
}

/** Whether a flag is a label keyword (well-formed). */
export function isLabelFlag(flag: string): boolean {
  return decodeLabel(flag) !== null;
}

/** Arabic-collated sort used for the rail label list. */
export function sortLabels(names: readonly string[]): string[] {
  return [...names].sort((a, b) => a.localeCompare(b, 'ar'));
}
